import { action, computed, observable, runInAction } from "mobx";

import EventViewStore from "../event/EventViewStore";
import { SportsStatsApiService } from "@api-services/sport-stats";
import { AdditionalOfferViewStore } from "../shared/AdditionalOfferViewStore";
import {
	IEventStatsBase,
	StatusCodes,
	MatchStatAvailabilityIndex,
	IEventPlayers,
	IPlayerStatGroup,
	PlayersByTeamFilter,
	PlayerSortConfig,
	ILivePlayerStatsAvailability,
	IEventPlayer,
} from "@data-types/sport-stats/SportStatsTypes";
import { Subscription } from "rxjs";
import { getCurrentLanguage } from "@utils";

export default class LivePlayerStatisticsStore {
	eventStore: EventViewStore | AdditionalOfferViewStore;

	@observable playerStatisticsRawData: IEventPlayers | null;
	@observable
	playerStatsGroups: IPlayerStatGroup[] = [];

	@observable isPlayerStatActive = false;
	@observable isFetching = false;
	@observable isPlayerStatsAvailable = false;

	@observable isError = false;

	@observable activeTab: PlayersByTeamFilter = "all";
	@observable playerFilter: string = "summary";

	private dataSubscription?: Subscription | null = null;
	private availabilitySubscription?: Subscription | null = null;

	private positionOrder: { [key: string]: number } = {
		GK: 1,
		DEF: 2,
		MID: 3,
		FWD: 4,
	};

	private sortPlayersByTeam(
		playerFilter: PlayersByTeamFilter,
		homeTeamPlayers: IEventPlayer[],
		awayTeamPlayers: IEventPlayer[]
	) {
		if (playerFilter === "home") {
			return homeTeamPlayers;
		}
		if (playerFilter === "away") {
			return awayTeamPlayers;
		}
		return homeTeamPlayers.concat(awayTeamPlayers);
	}

	private sortByMinutesPlayed(player: IEventPlayer) {
		return (
			player.statistics.summary?.minutesPlayed &&
			typeof player.statistics.summary?.minutesPlayed === "string" &&
			player.statistics.summary?.minutesPlayed.trim() !== ""
		);
	}

	private sortByRating(
		aValue: IEventPlayer,
		bValue: IEventPlayer,
		direction: "asc" | "desc"
	) {
		return direction === "asc"
			? aValue.rating - bValue.rating
			: bValue.rating - aValue.rating;
	}

	private sortByPosition(
		aValue: IEventPlayer,
		bValue: IEventPlayer,
		direction: "asc" | "desc"
	) {
		return direction === "asc"
			? this.positionOrder[aValue.position] -
					this.positionOrder[bValue.position]
			: this.positionOrder[bValue.position] -
					this.positionOrder[aValue.position];
	}

	private parseStatValue(value: string | number | boolean | null) {
		if (typeof value !== "string") value = String(value || "");

		return {
			main: parseFloat(value.match(/^(\d+)(?: \((\d+)\))?/)?.[1] || "0"),
			// some stats have values in parenthesis, so if main values are equal we compare the ones in parenthesis
			parenthesis: parseFloat(
				value.match(/^(\d+)(?: \((\d+)\))?/)?.[2] || "0"
			),
		};
	}

	private sortByStat(
		aValue: string | number | boolean | null,
		bValue: string | number | boolean | null,
		direction: "asc" | "desc"
	): number {
		const aMatch = this.parseStatValue(aValue);
		const bMatch = this.parseStatValue(bValue);
		if (aMatch.main !== bMatch.main) {
			return direction === "asc"
				? aMatch.main - bMatch.main
				: bMatch.main - aMatch.main;
		}
		return direction === "asc"
			? aMatch.parenthesis - bMatch.parenthesis
			: bMatch.parenthesis - aMatch.parenthesis;
	}

	constructor(eventStore: EventViewStore | AdditionalOfferViewStore) {
		this.eventStore = eventStore;
	}

	@action.bound
	transformPlayerStatisticsData() {
		if (!this.playerStatisticsRawData) {
			this.playerStatsGroups = [];
			return;
		}

		let groupMap = new Map();

		const allPlayers = [
			...this.playerStatisticsRawData.homeTeamPlayers,
			...this.playerStatisticsRawData.awayTeamPlayers,
		];

		allPlayers.forEach((player) => {
			if (!player.statistics) {
				return;
			}

			Object.keys(player.statistics).forEach((group: string) => {
				if (!groupMap.has(group)) {
					groupMap.set(group, {
						title: `EVENT.STATISTICS.PLAYER_STATS.PLAYER_STAT_TITLES.${group}.TITLE`,
						key: group,
						data: [],
					});
				}

				const groupData = groupMap.get(group).data;

				Object.keys(player.statistics[group] ?? {}).forEach(
					(stat: string) => {
						if (
							!groupData.some(
								(item: { key: string }) => item.key === stat
							)
						) {
							groupData.push({
								title: `EVENT.STATISTICS.PLAYER_STATS.PLAYER_STAT_TITLES.${group}.${stat}`,
								key: stat,
							});
						}
					}
				);
			});
		});

		const sortedGroupMap = new Map(
			Array.from(groupMap.entries()).sort(([keyA], [keyB]) => {
				if (keyA === "summary") return -1;
				if (keyB === "summary") return 1;
				return 0;
			})
		);

		this.playerStatsGroups = Array.from(sortedGroupMap.values());
	}

	@action.bound
	filterPlayers(
		playerFilter: PlayersByTeamFilter,
		statsGroupKey?: string,
		sortConfig?: PlayerSortConfig
	) {
		const { homeTeamPlayers, awayTeamPlayers } =
			this.playerStatisticsRawData!;

		let players = this.sortPlayersByTeam(
			playerFilter,
			homeTeamPlayers,
			awayTeamPlayers
		);

		players = players.filter((player) => this.sortByMinutesPlayed(player));

		if (statsGroupKey === "goalkeeper") {
			players = players.filter((player) => player.position === "GK");
		}

		if (sortConfig && statsGroupKey) {
			players = [...players].sort((a, b) => {
				if (sortConfig.key === "rating") {
					return this.sortByRating(a, b, sortConfig.direction);
				} else if (sortConfig.key === "position") {
					return this.sortByPosition(a, b, sortConfig.direction);
				}
				return this.sortByStat(
					a.statistics[statsGroupKey]?.[sortConfig.key],
					b.statistics[statsGroupKey]?.[sortConfig.key],
					sortConfig.direction
				);
			});
		}

		return players;
	}

	@computed
	get availablePlayerTabs() {
		const options: { key: string; title: string | undefined }[] = [];
		const data = this.playerStatisticsRawData!;
		const eventData = this.getEventData();

		if (!data || !eventData) return options;

		const homeTeam = this.eventStore.lookupsStore?.teams?.get(
			eventData.event.teamOneId || ""
		);
		const awayTeam = this.eventStore.lookupsStore?.teams?.get(
			eventData.event.teamTwoId || ""
		);

		if (data.homeTeamPlayers && data.awayTeamPlayers) {
			options.push({
				key: "all",
				title: "EVENT.HEADER.BOTH",
			});
		}
		if (data.homeTeamPlayers) {
			options.push({
				key: "home",
				title: homeTeam?.name,
			});
		}
		if (data.awayTeamPlayers) {
			options.push({
				key: "away",
				title: awayTeam?.name,
			});
		}
		return options;
	}

	@action.bound
	setActiveTab(activeTab: PlayersByTeamFilter) {
		this.activeTab = activeTab;
	}

	@action.bound
	setPlayerFilter(playerFilter: string) {
		this.playerFilter = playerFilter;
	}

	@action.bound
	togglePlayerStat() {
		this.activeTab = "all";
		this.playerFilter = "summary";
		this.isPlayerStatActive = !this.isPlayerStatActive;
	}

	@action.bound
	createPlayerStatsFilter<T extends "data" | "availability">(
		requestType: T
	): { RequestType: T; Events: IEventStatsBase[] } | null {
		const filter = this.generatePlayerStatsEventFilter();
		if (!filter) {
			return null;
		}

		return {
			RequestType: requestType,
			Events: [filter],
		};
	}

	@action.bound
	async fetchPlayerStatsAvailability() {
		const filter = this.createPlayerStatsFilter("availability");

		if (!filter) {
			return null;
		}

		try {
			return await SportsStatsApiService.fetchPlayerLiveStatistics({
				specific: filter,
			});
		} catch (error) {
			console.error(error);
			return null;
		}
	}

	@action.bound
	async fetchPlayerStats() {
		this.isFetching = true;

		const filter = this.createPlayerStatsFilter("data");
		if (filter == null) {
			return;
		}

		try {
			const playerStats =
				await SportsStatsApiService.fetchPlayerLiveStatistics({
					specific: filter,
				});

			if (playerStats.statusCode == StatusCodes.NOT_AVAILABLE) {
				throw new Error("Player live statistics not available.");
			}

			runInAction(() => {
				if (
					!playerStats?.response?.length ||
					!playerStats.response?.[0].data
				) {
					throw new Error("Player live statistics not available.");
				}

				if (playerStats.response != null) {
					const response = playerStats.response[0];

					if (response.data.eventPlayers != null) {
						this.isPlayerStatsAvailable = true;
						this.playerStatisticsRawData =
							response.data.eventPlayers;
						this.transformPlayerStatisticsData();
					}
					this.isError = false;
				}
			});
		} catch (error) {
			console.error(error);
			this.isError = true;
			this.isPlayerStatsAvailable = false;
		} finally {
			this.isFetching = false;
		}
	}

	@action.bound
	async initializeLivePlayerStatistics() {
		this.disposeDataSubscription();

		const filter = this.generateSubscriptionFilter("data");

		if (!filter) {
			return;
		}

		this.dataSubscription = this.eventStore.rootStore.sportStatHub
			?.getSubscription(filter)
			.subscribe({
				next: (data: any) => {
					if (Array.isArray(data) && data.length > 0) {
						const eventPlayers = data[0].data?.eventPlayers;

						if (
							!eventPlayers ||
							!Array.isArray(eventPlayers.awayTeamPlayers) ||
							!Array.isArray(eventPlayers.homeTeamPlayers)
						) {
							console.warn("Missing live player statistics");
							return;
						}

						this.playerStatisticsRawData = eventPlayers;
						this.transformPlayerStatisticsData();
					}
				},
				error: (e: any) => {
					console.log(e);
					this.isError = true;
				},
			});

		await this.fetchPlayerStats();

		this.transformPlayerStatisticsData();
	}

	generateSubscriptionFilter(requestType: "data" | "availability") {
		if (this.isError) {
			return;
		}

		const eventFilter = this.generatePlayerStatsEventFilter();

		if (!eventFilter) {
			return;
		}

		return {
			language: getCurrentLanguage(),
			subscriptionId:
				requestType == "data"
					? `player-stats-${eventFilter.EventId}`
					: `player-stats-availability-${eventFilter.EventId}`,
			environment: WEBPACK_ENVIRONMENT_KEY,
			typeAbrv: "EventPlayers",
			filterSpecific: {
				RequestType: requestType,
				Events: [
					{
						LastMinuteReceived: 0,
						...eventFilter,
					},
				],
			},
		};
	}

	generatePlayerStatsEventFilter(): IEventStatsBase | null {
		const eventData = this.getEventData();

		if (!eventData || !eventData.event.id) {
			console.warn("Unable to create filter, missing event data");
			return null;
		}


		if(this.eventStore?.isOfferHistory) {
			return {
				EventId: eventData.event.id,
				Lineups: true,
			};
		}
		
		return {
			EventId: eventData.event.id,
		};
	}

	@action.bound
	disposeDataSubscription() {
		if (this.dataSubscription != null) {
			this.dataSubscription.unsubscribe();
			this.dataSubscription = null;
		}
	}

	@action.bound
	disposeAvailabilitySubscription() {
		if (this.availabilitySubscription != null) {
			this.availabilitySubscription.unsubscribe();
			this.availabilitySubscription = null;
		}
	}

	@action.bound
	async checkLiveStatisticsAvailability() {
		if (!WEBPACK_IS_SPORT_STATISTICS_ENABLED) {
			return;
		}

		if (this.eventStore.sport?.abrv != "soccer") {
			return;
		}

		if (
			!this.eventStore.event?.liveStatus &&
			!this.eventStore?.isOfferHistory
		) {
			return;
		}

		const isLive = this.eventStore.event?.liveStatus;

		this.disposeAvailabilitySubscription();

		// first send http request for availability
		if (!this.isPlayerStatsAvailable) {
			const response = await this.fetchPlayerStatsAvailability();

			if (
				response &&
				response.statusCode !== StatusCodes.NOT_AVAILABLE &&
				response?.response?.[0]?.data?.eventPlayers ==
					MatchStatAvailabilityIndex.AVAILABLE
			) {
				if (isLive) {
					this.startLivePLayerStatSession();
				} else {
					await this.fetchPlayerStats();
				}
			}
		}

		// then subscribe to availability
		if (isLive) {
			this.subscribeToAvailability();
		}
	}

	@action.bound
	subscribeToAvailability() {
		const filter = this.generateSubscriptionFilter("availability");

		if (!filter) {
			return;
		}

		// subscribe to availability only if stats are closed and on event page opening
		if (!this.isPlayerStatActive) {
			this.availabilitySubscription =
				this.eventStore.rootStore.sportStatHub
					?.getSubscription(filter)
					.subscribe({
						next: (hubData: any) => {
							const response =
								hubData[0] as ILivePlayerStatsAvailability;
							if (
								response?.data?.eventPlayers &&
								response?.data?.eventPlayers ===
									MatchStatAvailabilityIndex.AVAILABLE
							) {
								this.startLivePLayerStatSession();
								return;
							}
							this.isPlayerStatsAvailable = false;
						},
						error: (e: any) => {
							console.log(e);
							this.isPlayerStatsAvailable = false;
							this.isError = true;
						},
					});
		}
	}

	startLivePLayerStatSession() {
		if (!this.isPlayerStatsAvailable) {
			this.isPlayerStatsAvailable = true;
			this.initializeLivePlayerStatistics();
		}
	}

	getEventData() {
		const store = this.eventStore;
		if (store.event && store.tournament) {
			return {
				event: store.event,
				tournament: store.tournament,
			};
		}
		return null;
	}

	@action.bound
	onDispose() {
		this.playerStatisticsRawData = null;
		this.playerStatsGroups = [];
		this.isFetching = false;
		this.isPlayerStatsAvailable = false;
		this.isError = false;
		this.disposeDataSubscription();
		this.disposeAvailabilitySubscription();
	}
}
